/**
 * Service which is used to signin and signout the application.
 */
import { EventEmitter, Injectable } from '@angular/core';
import { HttpRoutingService } from '../../common/services/http-routing.service';
import { BehaviorSubject, throwError, Subscription, Observable } from 'rxjs';
import { HeaderService } from '../../common/services/header.service';
import { map, catchError } from 'rxjs/operators';
import { configFile } from '../../../app/config-file';
import { ApiResponse, CurrentUser, User } from '../../common/models/common.model';
import { ChangePasswordReq, ConfigurePasswordReq, ForgotPasswordReq, GoogleResponse, LoginReq, RefreshTokenRes, ResendOTPReq, ResendOTPRes, SigninWithGoogleRes, SignupWithGoogle, VerifyOTPReq, VerifyTokenReq } from '../models/auth.model';
/**
 * Service which is used to signin and signout the application.
 */
@Injectable({
  providedIn: 'root'
})

export class AuthService {
  /**
   * Variable errorMessage is used to access the functions declare in the services globally.
   */
  errorMessages: Record<string, string> = {};
  /**
   * Variable which is used to define the properties of the current activated user.
   */
  user = new BehaviorSubject<User | null>(null);
  /**
   * Variable which is used to subscribe or unsubscribe the details.
   * @type{Subscription}
   */
  subscriptions: Subscription = new Subscription()
  /**
   * Variable which is used to define currently activated menu in the side navbar.
   */
  currentActiveMenu = new EventEmitter<string>();
  /**
   * Constructor used to inject the required services.
   * @param http to access the services provided in  HttpRoutingService
   * @param headerService To access and handle the httpHeaders in the request.
   * @param store To define the store.
   */
  constructor(
    private http: HttpRoutingService,
    private headerService: HeaderService
  ) { }
  /**
   * Method which is used to subscribe the data in the Json file and passed it to the global variable.
   * @param message checks the response and stored it in the array format.
   * @return {Observable<Record<string, string>>}
   */
  getMessages() {
    this.http.getJsonData('/formErrorMessage.json').subscribe(res => this.errorMessages = res);
  }
  /**
   * Method which is used to signin the application for the given login details.
   * @param data To define the login details.
   * @return {Observable<ApiResponse<CurrentUser>>}
   */
  signIn(data: LoginReq): Observable<ApiResponse<CurrentUser>> {
    return this.http.postMethod('login', data).pipe(map((res: ApiResponse<CurrentUser>) => {
      if (res.success && res.data && res.data.user && res.data.token) {
        this.user.next(res.data.user);
        sessionStorage.setItem('currentUserToken', JSON.stringify({ token: res.data.token, refreshToken: res.data.refreshToken }));
      }
      return res;
    }), catchError(err => {
      return throwError(() => err);
    }));
  }
  /**
   * Method which is used to check whether the current user is authenticated or not.
   * @return {boolean}
   */
  isAuthenticated(): boolean {
    const stored = sessionStorage.getItem('currentUserToken');
    const currentUser = stored ? JSON.parse(stored) : null;
    return !!(currentUser && currentUser.token);
  }
  /**
   * Method which used to check whether the user is logged in (user) or not.
   * @param id To define the user id.
   */
  isLoggedUser(id: number): boolean {
    const currentUser = this.user?.value;
    return currentUser ? +id === +currentUser.id : false;
  }
  /**
   * Method which is used to check whether the logged in user role is admin or not.
   */
  // isAdmin(): boolean {
  //   const user = this.user.value;
  //   return user?.role?.name === configFile.defaultRole.admin;
  // }
  /**
   * Method which is used to check whether the logged in user is an account owner or not.
   */
  isAccountOwner(): boolean {
    const user = this.user.value;
    return !!user?.accountOwner;
  }
  /**
   * Method which is used to get the token of current user.
   * @return {string}
   */
  getToken(): string {
    const stored = sessionStorage.getItem('currentUserToken');
    const currentUser = stored ? JSON.parse(stored) : null;
    return currentUser?.token || '';
  }
  /**
   * Method which is used to get the refresh token form server side.
   * @return {Observable<ApiResponse<RefreshTokenRes>>}
   */
  getRefreshToken(): Observable<ApiResponse<RefreshTokenRes>> {
    const currentUser = JSON.parse(sessionStorage.getItem('currentUserToken') || '');
    const refreshToken: string | null = currentUser ? currentUser.refreshToken : null;
    return this.http.postMethod('refreshToken', { refreshToken }) as Observable<ApiResponse<RefreshTokenRes>>;
  }
  /**
   * Method which is used to signout the user.
   */
  logout() {
    // Remove the current user details from local storage.
    sessionStorage.removeItem('currentUserToken');
    // Remove the authentication details from the request.
    this.headerService.clearHeaders('default', 'Authorization');
    // this.store.dispatch({ type: 'RESET_STATE' });
    return true;
  }
  /**
   * Method which is used to check whether the token is expired or not.
   * @param data The data which holds the token.
   * @return {Observable<ApiResponse<null>>}
   */
  verifyToken(data: VerifyTokenReq): Observable<ApiResponse<null>> {
    return this.http.postMethod('verifyToken', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to login the application.
   * @param data To define the login details.
   * @return {Observable<ApiResponse<CurrentUser | SigninWithGoogleRes>>}
   */
  signinWithGoogle(data: GoogleResponse): Observable<ApiResponse<CurrentUser | SigninWithGoogleRes>> {
    return this.http.postMethod('signinWithGoogle', data).pipe(map((res: ApiResponse<CurrentUser | SigninWithGoogleRes>) => {
      const currentUser = res.data as CurrentUser;
      if (res.success && currentUser && currentUser.user && currentUser.token) {
        this.user.next(currentUser.user);
        sessionStorage.setItem('currentUserToken', JSON.stringify({ token: currentUser.token, refreshToken: currentUser.refreshToken }));
      }
      return res;
    }), catchError(err => {
      return throwError(() => err);
    }));
  }
  /**
   * Method which is used to sign up in the application.
   * @param data To define the company details.
   * @return {Observable<ApiResponse<CurrentUser>>}
   */
  signUpWithGoogle(data: SignupWithGoogle): Observable<ApiResponse<CurrentUser>> {
    return this.http.postMethod('signupWithGoogle', data).pipe(map((res: ApiResponse<CurrentUser>) => {
      if (res.success && res.data && res.data.user && res.data.token) {
        this.user.next(res.data.user);
        sessionStorage.setItem('currentUserToken', JSON.stringify({ token: res.data.token, refreshToken: res.data.refreshToken }));
      }
      return res;
    }), catchError(err => {
      return throwError(() => err);
    }));
  }
  /**
   * Method which is used to set the password of the employee.
   * @param data The data which holds the new password and token of the employee.
   * @return {Observable<ApiResponse<null>>}
   */
  configurePassword(data: ConfigurePasswordReq): Observable<ApiResponse<null>> {
    return this.http.postMethod('configurePassword', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to reset the password of the employee.
   * @param data The data which holds the old password and new password of the employee.
   * @return {Observable<ApiResponse<null>>}
   */
  changePassword(data: ChangePasswordReq): Observable<ApiResponse<null>> {
    return this.http.postMethod('changePassword', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to send the forgot password link to provided mail id.
   * @param data The data which holds the user mail to be sent mail.
   * @return {Observable<ApiResponse<null>>}
   */
  forgotPassword(data: ForgotPasswordReq): Observable<ApiResponse<null>> {
    return this.http.postMethod('forgotPassword', data) as Observable<ApiResponse<null>>;
  }
  /**
   * Method which is used to resend OTP.
   * @param data which holds the user detail.
   * @returns {Observable<ApiResponse<ResendOTPRes>>}
   */
  resendOTP(data: ResendOTPReq): Observable<ApiResponse<ResendOTPRes>> {
    return this.http.postMethod('resendOTP', data) as Observable<ApiResponse<ResendOTPRes>>;
  }
  /**
   * Method which is used to verify OTP.
   * @param data which holds the user detail.
   * @returns {Observable<ApiResponse<CurrentUser>>}
   */
  verifyOTP(data: VerifyOTPReq): Observable<ApiResponse<CurrentUser>> {
    return this.http.postMethod('verifyOTP', data).pipe(map((res: ApiResponse<CurrentUser>) => {
      if (res.success && res.data && res.data.user && res.data.token) {
        this.user.next(res.data.user);
        sessionStorage.setItem('currentUserToken', JSON.stringify({ token: res.data.token, refreshToken: res.data.refreshToken }));
      }
      return res;
    }), catchError(err => {
      return throwError(() => err);
    }));
  }
  /**
   * Method which is used to check whether the logged in user role is super admin or not.
   */
  showPaymentPage(): boolean {
    const response = this.user.value;
    return response ? !!response.showPlanUpgrade : false;
  }
}
